/**
 * ScannerReadout (D-331, 2026-07-31) — the Intrusion Scanner card's OWN small
 * surface. GA-08/D-316 originally wired `intel.revealWaves` onto the
 * `GameBanner` (the only moment surface that existed then); the owner's
 * grid re-route audit called that out specifically: the payload is a
 * multi-line READING cue (up to 3 wave compositions), not a message — it
 * never fit `GameBanner`'s single-line canvas `fillText` (newlines never
 * rendered; the old "\n"-joined body silently ran together on one line) and
 * it doesn't fit the TV's 2-line chyron either (D-329's own contract).
 *
 * Pure DOM/CSS, render-only, read-only (never mutates run state — the card
 * stays usable mid-wave). `pointer-events: none` so it never blocks a
 * build/deploy click; auto-dismisses after a read-length hold; announce()s
 * once for the a11y mirror (BL-110), matching GameBanner's contract.
 */
import { COLORS } from "../core/constants.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { UI_FONT_STACK, UI_Z, roleFontSize, withUiScale } from "./uiTokens.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { announce } from "./a11y.ts";

const STYLE_ID = "scanner-readout-styles";
/** A reading cue, not a flash — long enough to actually read 3 lines. */
const HOLD_MS = 7000;
const FADE_MS = 300;

export class ScannerReadout {
  private readonly parent: HTMLElement;
  private el: HTMLElement | null = null;
  private hideTimer: ReturnType<typeof setTimeout> | null = null;
  private fadeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(parent: HTMLElement) {
    this.parent = parent;
    ScannerReadout.ensureStyles();
  }

  /** Show the deep-scan readout: a header line + up to 3 wave-composition
   *  rows (or the "no further manifests" line at the run's tail). Re-showing
   *  replaces the panel and restarts the hold. */
  show(header: string, lines: string[]): void {
    this.dismiss();

    const el = document.createElement("div");
    el.className = "scanner-readout";
    if (isReducedMotion()) el.classList.add("scanner-readout-reduced");
    el.setAttribute("aria-hidden", "true"); // announce() carries the a11y copy

    const head = document.createElement("div");
    head.className = "sr-head";
    head.textContent = header;
    el.appendChild(head);

    for (const line of lines) {
      const row = document.createElement("div");
      row.className = "sr-line";
      row.textContent = line;
      el.appendChild(row);
    }

    this.parent.appendChild(el);
    this.el = el;
    announce([header, ...lines].join(". "));

    this.hideTimer = setTimeout(() => {
      el.classList.add("sr-out");
      this.fadeTimer = setTimeout(() => this.dismiss(), FADE_MS);
    }, HOLD_MS);
  }

  dismiss(): void {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (this.fadeTimer !== null) {
      clearTimeout(this.fadeTimer);
      this.fadeTimer = null;
    }
    this.el?.remove();
    this.el = null;
  }

  private static ensureStyles(): void {
    if (typeof document === "undefined" || document.getElementById(STYLE_ID)) {
      return;
    }
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = `
      .scanner-readout {
        position: absolute; left: 50%; top: 10%; z-index: ${UI_Z.OVERLAY};
        transform: translateX(-50%);
        pointer-events: none;
        width: min(420px, 78%);
        box-sizing: border-box;
        padding: 10px 14px 12px;
        font-family: ${UI_FONT_STACK};
        background: ${PANEL.BG};
        border: 1px solid color-mix(in srgb, ${COLORS.ICE_CYAN} 55%, transparent);
        border-left: 3px solid ${COLORS.ICE_CYAN};
        border-radius: 5px;
        box-shadow: 0 8px 26px -8px black,
          0 0 18px color-mix(in srgb, ${COLORS.ICE_CYAN} 20%, transparent);
        color: ${COLORS.TEXT_PRIMARY};
        opacity: 1;
        animation: sr-in 0.24s ease-out both;
      }
      .scanner-readout .sr-head {
        color: ${COLORS.ICE_CYAN}; font-weight: bold;
        font-size: ${roleFontSize("label")}; letter-spacing: 0.06em;
        border-bottom: 1px solid color-mix(in srgb, ${COLORS.ICE_CYAN} 25%, transparent);
        padding-bottom: 4px; margin-bottom: 5px;
      }
      .scanner-readout .sr-line {
        /* Dimmed body text: the central ICE_CYAN read toward white so a long
           preview list reads calmer than the accent header (no new hex). */
        color: color-mix(in srgb, ${COLORS.ICE_CYAN} 60%, ${COLORS.TEXT_PRIMARY});
        font-size: ${withUiScale(15)}; line-height: 1.5;
      }
      .scanner-readout.sr-out { animation: sr-out ${FADE_MS}ms ease-in forwards; }
      @keyframes sr-in { from { opacity: 0; transform: translateX(-50%) translateY(-6px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
      @keyframes sr-out { to { opacity: 0; } }
      .scanner-readout.scanner-readout-reduced { animation: none; opacity: 1; }
      .scanner-readout.scanner-readout-reduced.sr-out { animation: none; opacity: 0; transition: opacity ${FADE_MS}ms ease-in; }
      @media (prefers-reduced-motion: reduce) {
        .scanner-readout { animation: none; }
        .scanner-readout.sr-out { animation: none; opacity: 0; transition: opacity ${FADE_MS}ms ease-in; }
      }
    `;
    document.head.appendChild(style);
  }
}
